import { Result, err, ok } from 'neverthrow'
import { getChatApiRoute } from '../api/v1Client'
import { arkoseChallengeError, type ChatTurnError } from '../engine/errors'
import { traceTurnEvent } from '../engine/telemetry'
import { logAmplitudeEvent } from '../utils/amplitude'
import AuthIpcRenderer from '@renderer/ipc/AuthIpc.renderer'
import ChallengeIpcRenderer from '@renderer/ipc/ChallengeIpc.renderer'
import GlobalStateUtils from '@shared/ipc/GlobalStateUtils'
import GlobalLogger from '@shared/utils/logger/GlobalLogger'
import {
  CHALLENGE_PREFETCH_MAX_AGE_MS,
  challengeFailure,
  isFixedByReloadingArkose,
  type ChallengeResult,
} from '@shared/challenge/types'
import type { DataExchangeFailure } from '@shared/chat-telemetry/turn-events'

const log = GlobalLogger.bind('SendChallenge')

/**
 * Each payload request's budget. It starts after the token lookup, so a slow
 * refresh after the laptop wakes can't spend it. A retry after a 401 spends it
 * on the token refresh too. The route answers within 1 s at the gateway, and a
 * cold first send has taken 1.6 s at the client.
 */
const DATA_EXCHANGE_REQUEST_TIMEOUT_MS = 2_000

/**
 * A run makes one retry, so it waits at most 4 s on payload requests. A send
 * that waited through its background run's failed requests makes one of its
 * own, so the worst case for a send is 6 s.
 */
const DATA_EXCHANGE_MAX_REQUESTS = 2

type DataExchangeResponse = { blob: string; failure: undefined } | { blob: null; failure: DataExchangeFailure }

function withDeadline(signal: AbortSignal | undefined, deadline: AbortSignal): AbortSignal {
  return signal ? AbortSignal.any([signal, deadline]) : deadline
}

async function requestDataExchangeBlob(
  accessToken: string,
  signal: AbortSignal | undefined,
  deadline: AbortSignal,
): Promise<DataExchangeResponse> {
  try {
    const response = await fetch(getChatApiRoute('api/v1/chat/challenge-payload'), {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
      signal: withDeadline(signal, deadline),
    })

    if (!response.ok) {
      return { blob: null, failure: `http-${response.status}` }
    }

    const body = (await response.json()) as { blob?: string | null }
    // The route also answers null when minting failed under a usable key, so a
    // null doesn't show that the gateway won't require a blob.
    return body.blob ? { blob: body.blob, failure: undefined } : { blob: null, failure: 'not-issued' }
  } catch (cause) {
    if (signal?.aborted) {
      return { blob: null, failure: 'cancelled' }
    }
    if (deadline.aborted) {
      return { blob: null, failure: 'timeout' }
    }
    return { blob: null, failure: cause instanceof SyntaxError ? 'malformed' : 'network' }
  }
}

/**
 * The forced refresh behind `useAuth().handleUnauthorized`, called over IPC
 * because callers here only pass `getAuthToken`. Null if it fails or `signal`
 * aborts first.
 */
async function refreshAuthToken(rejectedToken: string, signal: AbortSignal): Promise<string | null> {
  const abandoned = new Promise<null>((resolve) => {
    if (signal.aborted) resolve(null)
    signal.addEventListener('abort', () => resolve(null), { once: true })
  })
  const refreshed = AuthIpcRenderer.handleUnauthorized(rejectedToken).catch((cause: unknown) => {
    log.warn(`Token refresh for the Data Exchange retry failed: ${String(cause)}`)
    return null
  })
  return await Promise.race([refreshed, abandoned])
}

/**
 * The token to retry a failed request with, or null when a retry would fail the
 * same way. A 429 isn't retried. The rate limit is per user across routes, so
 * an immediate retry would be refused too.
 */
async function tokenForRetry(
  failure: DataExchangeFailure,
  rejectedToken: string,
  signal: AbortSignal,
): Promise<string | null> {
  if (failure === 'http-401') {
    return await refreshAuthToken(rejectedToken, signal)
  }
  const isTransient = failure === 'timeout' || failure === 'network' || failure.startsWith('http-5')
  return isTransient ? rejectedToken : null
}

type DataExchangeFetch = DataExchangeResponse & { attempts: number }

/**
 * Gets the Arkose Data Exchange payload for the next challenge. Never rejects.
 *
 * The gateway mints the blob, not this process. It is encrypted under a shared
 * secret and carries our own verdict on the user, so a client that could build
 * one could also claim `user_suspicious: '0'` for itself.
 *
 * Where `arkose-token-requirement` applies, the gateway refuses a token minted
 * without a blob, so a failed request gets a retry when `maxRequests` allows. A
 * failed token lookup isn't retried, because the turn POST hits the same failure.
 */
async function fetchDataExchangeBlob(
  getAuthToken: () => Promise<string>,
  signal: AbortSignal | undefined,
  maxRequests: number,
): Promise<DataExchangeFetch> {
  let accessToken: string
  try {
    accessToken = await getAuthToken()
  } catch (cause) {
    log.warn(`No auth token for the Data Exchange payload: ${String(cause)}`)
    return { blob: null, failure: 'no-token', attempts: 0 }
  }

  const first = await requestDataExchangeBlob(
    accessToken,
    signal,
    AbortSignal.timeout(DATA_EXCHANGE_REQUEST_TIMEOUT_MS),
  )
  if (!first.failure || maxRequests < 2) {
    return { ...first, attempts: 1 }
  }
  const retryDeadline = AbortSignal.timeout(DATA_EXCHANGE_REQUEST_TIMEOUT_MS)
  const retryToken = await tokenForRetry(first.failure, accessToken, withDeadline(signal, retryDeadline))
  if (retryToken === null) {
    return { ...first, attempts: 1 }
  }
  return { ...(await requestDataExchangeBlob(retryToken, signal, retryDeadline)), attempts: 2 }
}

/** One Arkose run, and how its Data Exchange fetch went. */
type ChallengeRun = {
  result: ChallengeResult
  dataExchangeMs: number
  hasDataExchange: boolean
  dataExchangeAttempts: number
  dataExchangeFailure: DataExchangeFailure | undefined
  /** An earlier run stalled or Arkose errored, and this one ran after main reloaded Arkose. */
  retriedOnReloadedArkose: boolean
}

type ChallengeRunRequest = {
  runId: string
  getAuthToken: () => Promise<string>
  signal: AbortSignal | undefined
  background: boolean
  gatewayRequiresToken?: boolean
  maxDataExchangeRequests: number
}

/**
 * Fetch a fresh Data Exchange blob and run Arkose with it. Never rejects.
 *
 * A foreground run whose fetch failed runs without the blob and leaves the
 * gateway to decide. A background run stops there, since the send won't spend
 * a token minted without one.
 */
async function runChallenge({
  runId,
  getAuthToken,
  signal,
  background,
  gatewayRequiresToken,
  maxDataExchangeRequests,
}: ChallengeRunRequest): Promise<ChallengeRun> {
  const startedAt = Date.now()
  const dataExchange = await fetchDataExchangeBlob(getAuthToken, signal, maxDataExchangeRequests)
  const dataExchangeMs = Date.now() - startedAt
  let result: ChallengeResult
  // An abort during the fetch above cancelled a run main has never heard of.
  // Don't start it now.
  if (signal?.aborted) {
    result = challengeFailure('cancelled', 'The turn was cancelled before the challenge started.')
  } else if (background && dataExchange.failure) {
    result = challengeFailure('unavailable', 'The Data Exchange payload could not be fetched.')
  } else {
    try {
      result = await ChallengeIpcRenderer.getToken({
        runId,
        dataExchangeBlob: dataExchange.blob,
        background,
        gatewayRequiresToken,
      })
    } catch (cause) {
      // Only reachable if the IPC transport fails; the service returns challenge
      // failures on the success channel.
      result = challengeFailure('unavailable', String(cause))
    }
  }
  return {
    result,
    dataExchangeMs,
    hasDataExchange: dataExchange.blob !== null,
    dataExchangeAttempts: dataExchange.attempts,
    dataExchangeFailure: dataExchange.failure,
    retriedOnReloadedArkose: false,
  }
}

/**
 * Run the check, and once more if Arkose stalled or errored. Main reloads
 * Arkose after either, so the second run gets its current script along with a
 * fresh blob. It keeps the run id, so a cancel still reaches it.
 */
async function runChallengeWithReloadRetry(request: ChallengeRunRequest): Promise<ChallengeRun> {
  const first = await runChallenge(request)
  if (!isFixedByReloadingArkose(first.result)) {
    return first
  }
  return { ...(await runChallenge(request)), retriedOnReloadedArkose: true }
}

/**
 * A token minted while the user types. The send skips the blob fetch and the
 * Arkose round trip, about 0.5 s warm and nearer 2 s on the first send after
 * launch.
 */
type PrefetchedChallenge = {
  runId: string
  startedAt: number
  run: Promise<ChallengeRun>
  isSettled: boolean
  expiryTimer: ReturnType<typeof setTimeout>
  /** Stops a run still fetching its blob, before main has heard of it. */
  controller: AbortController
}

let prefetched: PrefetchedChallenge | null = null

/**
 * One background run per draft. Each run is a billed Arkose assessment, so a
 * failed run is not replaced on the next keystroke; only an expired token is.
 */
let hasPrefetchedForDraft = false

/**
 * Main runs one check at a time, so a background run started now would queue
 * ahead of a send that may still need to show a puzzle.
 */
let sendChallengesInFlight = 0

/**
 * Start minting the next send's token in the background. Called as the user
 * types; a no-op once this draft has had its run, or while a send is being
 * checked.
 */
export function prefetchSendChallenge(getAuthToken: () => Promise<string>) {
  if (hasPrefetchedForDraft || sendChallengesInFlight > 0) {
    return
  }
  hasPrefetchedForDraft = true

  const runId = crypto.randomUUID()
  const controller = new AbortController()
  const entry: PrefetchedChallenge = {
    runId,
    startedAt: Date.now(),
    run: runChallengeWithReloadRetry({
      runId,
      getAuthToken,
      signal: controller.signal,
      background: true,
      maxDataExchangeRequests: DATA_EXCHANGE_MAX_REQUESTS,
    }),
    isSettled: false,
    expiryTimer: setTimeout(() => {
      if (prefetched === entry) discardPrefetchedChallenge()
    }, CHALLENGE_PREFETCH_MAX_AGE_MS),
    controller,
  }
  void entry.run.then(() => {
    entry.isSettled = true
  })
  prefetched = entry
}

/** End a background run wherever it is: fetching its blob, queued in main, or running. */
function cancelPrefetchedRun(entry: PrefetchedChallenge) {
  entry.controller.abort()
  void ChallengeIpcRenderer.cancel({ runId: entry.runId })
}

/** Drop the held token and let the draft mint another. */
function discardPrefetchedChallenge() {
  const entry = prefetched
  if (!entry) {
    return
  }
  prefetched = null
  hasPrefetchedForDraft = false
  clearTimeout(entry.expiryTimer)
  cancelPrefetchedRun(entry)
}

// The blob names the user it was minted for, so a token must not outlive the
// account that typed it. Keyed on the user id: token refreshes re-emit sign-in,
// and an org switch keeps the user, so neither costs a held token.
GlobalStateUtils.user.onUserIdChange(() => discardPrefetchedChallenge())

/** Take the held token for this send, if it is still young enough to verify. */
function claimPrefetchedChallenge(): PrefetchedChallenge | null {
  const entry = prefetched
  if (!entry) {
    return null
  }
  // Catches an expiry timer that a sleeping machine delayed.
  if (Date.now() - entry.startedAt > CHALLENGE_PREFETCH_MAX_AGE_MS) {
    discardPrefetchedChallenge()
    return null
  }
  prefetched = null
  clearTimeout(entry.expiryTimer)
  return entry
}

/** A background run the send claimed, once it settled. */
type ClaimedRun = {
  run: ChallengeRun
  wasSettledWhenClaimed: boolean
}

/** Claim the held background run and wait for it, unless there is none. */
async function takePrefetchedRun(signal: AbortSignal | undefined): Promise<ClaimedRun | null> {
  const claimed = claimPrefetchedChallenge()
  if (!claimed) {
    return null
  }
  const wasSettledWhenClaimed = claimed.isSettled
  return { run: await awaitPrefetchedRun(claimed, signal), wasSettledWhenClaimed }
}

/** Wait for a claimed background run, giving up if the send is cancelled first. */
async function awaitPrefetchedRun(entry: PrefetchedChallenge, signal: AbortSignal | undefined): Promise<ChallengeRun> {
  if (!signal) {
    return await entry.run
  }
  const cancelled = new Promise<ChallengeRun>((resolve) => {
    const onAbort = () => {
      cancelPrefetchedRun(entry)
      resolve({
        result: challengeFailure('cancelled', 'The turn was cancelled while its challenge was running.'),
        dataExchangeMs: 0,
        hasDataExchange: false,
        dataExchangeAttempts: 0,
        dataExchangeFailure: undefined,
        retriedOnReloadedArkose: false,
      })
    }
    if (signal.aborted) onAbort()
    else signal.addEventListener('abort', onAbort, { once: true })
    void entry.run.then(() => signal.removeEventListener('abort', onAbort))
  })
  return await Promise.race([entry.run, cancelled])
}

/**
 * Run the check again after a failed background run, unless the send already
 * waited through that failure and a second run would double a slow timeout. A
 * puzzle always goes to a foreground run, the only kind that can show it, and
 * so does `disabled`, which was the flag when the run started, not now. So
 * does a run that couldn't fetch its payload, since the gateway may refuse a
 * token minted without one; the foreground run fetches a fresh one.
 */
function shouldRunAgain({ run, wasSettledWhenClaimed }: ClaimedRun): boolean {
  if (run.dataExchangeFailure) {
    return true
  }
  const { result } = run
  if (result.ok) {
    return false
  }
  return result.reason === 'puzzle-required' || result.reason === 'disabled' || wasSettledWhenClaimed
}

/** One request, not two, once the send has waited through a background run's failed ones. */
function dataExchangeRequestsLeft(background: ClaimedRun | null): number {
  const waitedThroughFailure =
    background !== null && background.run.dataExchangeFailure !== undefined && !background.wasSettledWhenClaimed
  return waitedThroughFailure ? 1 : DATA_EXCHANGE_MAX_REQUESTS
}

type ChallengeTiming = Omit<ChallengeRun, 'result'> & {
  /** How long the send waited for the check. */
  durationMs: number
  /** The token was minted in the background before the send. */
  prefetched: boolean
  backgroundDataExchangeFailure: DataExchangeFailure | undefined
}

/**
 * Every check goes on the turn trace. Product analytics only hears about the
 * ones that put the window on screen, since those interrupted the user.
 */
function recordChallenge(chatId: string, turnId: string | undefined, result: ChallengeResult, timing: ChallengeTiming) {
  const outcome = result.ok ? 'passed' : result.reason
  const shown = result.shown ?? false
  const warnings = result.warnings ?? 0
  traceTurnEvent({
    kind: 'challenge:result',
    chatId,
    turnId,
    outcome,
    shown,
    suppressed: result.suppressed ?? false,
    warnings,
    ...timing,
  })
  if (shown) {
    logAmplitudeEvent('Arkose Challenge Shown', {
      outcome,
      durationMs: timing.durationMs,
      warnings,
      conversationId: chatId,
    })
  }
}

export type SendChallengeParams = {
  chatId: string
  turnId: string | undefined
  signal: AbortSignal | undefined
  getAuthToken: () => Promise<string>
  /**
   * The gateway refused the turn for want of a valid token. Main runs the check
   * even with its flag off, and the send runs its own rather than take a held one.
   */
  gatewayRequiresToken?: boolean
}

/** Run the check in the foreground, where a puzzle can be shown and the send's abort reaches it. */
async function runForegroundChallenge({
  signal,
  getAuthToken,
  gatewayRequiresToken,
  maxDataExchangeRequests,
  retriesOnReloadedArkose,
}: Pick<SendChallengeParams, 'signal' | 'getAuthToken' | 'gatewayRequiresToken'> & {
  maxDataExchangeRequests: number
  retriesOnReloadedArkose: boolean
}): Promise<ChallengeRun> {
  // The challenge runs in main's window, so an abort has to be forwarded or a
  // cancelled turn leaves a puzzle on screen with nobody waiting on it. The run
  // id lets main end this run and no other, whether it is on screen or still
  // queued behind one.
  const runId = crypto.randomUUID()
  const onAbort = () => void ChallengeIpcRenderer.cancel({ runId })
  signal?.addEventListener('abort', onAbort, { once: true })
  const request = { runId, getAuthToken, signal, background: false, gatewayRequiresToken, maxDataExchangeRequests }
  try {
    return retriesOnReloadedArkose ? await runChallengeWithReloadRetry(request) : await runChallenge(request)
  } finally {
    signal?.removeEventListener('abort', onAbort)
  }
}

/**
 * Get the Arkose token a send needs: the one minted while the user typed if
 * there is one, otherwise a run now. `undefined` means send without one: main
 * found the flag off after this renderer saw it on, and the kill switch wins.
 */
export async function runSendChallenge({
  chatId,
  turnId,
  signal,
  getAuthToken,
  gatewayRequiresToken = false,
}: SendChallengeParams): Promise<Result<string | undefined, ChatTurnError>> {
  const startedAt = Date.now()
  sendChallengesInFlight += 1
  let run: ChallengeRun | null = null
  let background: ClaimedRun | null = null
  let prefetchedRun = false
  try {
    // A held token belongs to the draft typed since the refused send, and was
    // minted where no puzzle could show.
    background = gatewayRequiresToken ? null : await takePrefetchedRun(signal)
    if (background && !shouldRunAgain(background)) {
      run = background.run
      prefetchedRun = true
    }
    run ??= await runForegroundChallenge({
      signal,
      getAuthToken,
      gatewayRequiresToken,
      maxDataExchangeRequests: dataExchangeRequestsLeft(background),
      // A background run that already retried on reloaded Arkose has had its second chance.
      retriesOnReloadedArkose: !background?.run.retriedOnReloadedArkose,
    })
  } finally {
    sendChallengesInFlight -= 1
    // After a refusal the composer holds the next draft, whose run is its own.
    if (!gatewayRequiresToken) hasPrefetchedForDraft = false
  }

  const { result, ...dataExchange } = run
  recordChallenge(chatId, turnId, result, {
    durationMs: Date.now() - startedAt,
    ...dataExchange,
    prefetched: prefetchedRun,
    backgroundDataExchangeFailure: background?.run.dataExchangeFailure,
    retriedOnReloadedArkose: run.retriedOnReloadedArkose || (background?.run.retriedOnReloadedArkose ?? false),
  })

  if (result.ok) {
    return ok(result.token)
  }
  if (result.reason === 'disabled') {
    return ok(undefined)
  }
  return err(arkoseChallengeError(chatId, result.reason, result.message))
}
